import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MailOpen, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Messages() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-messages"],
    queryFn: async () => (await api.get("/admin/messages")).data,
  });

  const toggleRead = async (m) => {
    try {
      await api.patch(`/admin/messages/${m.id}`, { read: !m.read });
      qc.invalidateQueries({ queryKey: ["admin-messages"] });
      qc.invalidateQueries({ queryKey: ["admin-analytics"] });
    } catch {
      toast.error("Failed to update message");
    }
  };

  return (
    <div data-testid="admin-messages">
      <h1 className="font-heading font-bold text-2xl tracking-tight mb-6">Contact Messages</h1>
      {isLoading ? (
        <p className="text-slateblue text-sm">Loading…</p>
      ) : (data || []).length === 0 ? (
        <div className="bg-white border border-sandline p-12 text-center text-slateblue" data-testid="messages-empty">No messages yet.</div>
      ) : (
        <div className="space-y-3">
          {data.map((m) => {
            const digits = (m.phone || "").replace(/\D/g, "");
            const waPhone = digits.length === 10 ? `1${digits}` : digits;
            return (
              <div key={m.id} data-testid={`message-${m.id.slice(0, 8)}`}
                className={`bg-white border p-5 ${m.read ? "border-sandline" : "border-orange/50 shadow-[0_8px_24px_-16px_rgba(217,93,57,0.4)]"}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 ${m.read ? "text-slateblue" : "text-orange"}`}>
                      {m.read ? <MailOpen size={18} /> : <Mail size={18} />}
                    </span>
                    <div>
                      <p className="font-bold text-sm">{m.name} <span className="font-normal text-slateblue">· {m.email}</span></p>
                      <p className="text-xs text-slateblue mt-0.5">{new Date(m.created_at).toLocaleString()} {m.phone && `· ${m.phone}`} · {m.lang?.toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {digits && (
                      <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noopener noreferrer" data-testid={`message-wa-${m.id.slice(0, 8)}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#25D366] hover:opacity-80 uppercase tracking-wide">
                        <MessageCircle size={14} /> WhatsApp
                      </a>
                    )}
                    <button onClick={() => toggleRead(m)} data-testid={`message-toggle-${m.id.slice(0, 8)}`}
                      className="text-xs font-bold text-orange hover:text-orange-bright uppercase tracking-wide">
                      {m.read ? "Mark unread" : "Mark read"}
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-navy/90 whitespace-pre-line pl-8">{m.message}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
